import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../hooks/useApi';
import { 
    DocumentTextIcon, 
    ClipboardDocumentListIcon,
    AcademicCapIcon,
    CalendarIcon, 
    CheckBadgeIcon, 
    ClockIcon, 
    BookOpenIcon, 
    MagnifyingGlassIcon,
    FunnelIcon,
    UserCircleIcon,
    Squares2X2Icon,
    ListBulletIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import DateRangePicker from '../common/DateRangePicker';
import FilterDropdown from '../common/FilterDropdown';
import PremiumTooltip from '../common/PremiumTooltip';
import { sanitizeText } from '../../utils/lessonText';
import { generateRecordOfWorkPDF } from '../../utils/pdfGenerators';
import { useAppState } from '../../context/AppStateContext';

const RecordOfWorkTab = ({ courseId, isHOS = false, subTab = 'records', onSubTabChange }) => {
    const { get } = useApi();
    const { showToast } = useAppState();
    const navigate = useNavigate();

    // Opens the plan editor and tells it where the back button should return to
    const openPlanEditor = (planId) => {
        navigate(`/teacher/lesson-plan/editor/${planId}`, {
            state: courseId
                ? { returnTo: `/teacher/courses/${courseId}?tab=records&sub=records` }
                : undefined
        });
    };

    const handleExportRecord = async (record) => {
        try {
            await generateRecordOfWorkPDF(record);
            if (showToast) showToast('Record of work exported successfully!', 'success');
        } catch (err) {
            console.error('Failed to export record of work:', err);
            if (showToast) showToast('Failed to export record of work.', 'error');
        }
    };
    
    // Core States
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    
    // Filter States
    const [selectedLearningArea, setSelectedLearningArea] = useState('all');
    const [selectedGradeStream, setSelectedGradeStream] = useState('all');
    const [dateRange, setDateRange] = useState();
    const [selectedWeek, setSelectedWeek] = useState('');
    
    // Data States
    const [learningAreas, setLearningAreas] = useState([]);

    useEffect(() => {
        if (isHOS) {
            get('/api/cbc/learning-areas/')
                .then(res => setLearningAreas(res.results || res || []))
                .catch(err => console.error('Failed to fetch learning areas:', err));
        }
    }, [isHOS, get]);

    const { data: records, isLoading } = useQuery({
        queryKey: isHOS ? ['records-of-work', 'hos-all'] : ['records-of-work', courseId],
        queryFn: () => get(isHOS ? '/api/cbc/records-of-work/' : `/api/cbc/records-of-work/?learning_area=${courseId}`),
        enabled: isHOS || !!courseId
    });

    // 1. Learning Area Filter Options (Pure subject names)
    const learningAreaOptions = useMemo(() => {
        const rawRecords = records?.results || records || [];
        const names = new Set();
        (learningAreas || []).forEach(la => { if (la.name) names.add(la.name); });
        rawRecords.forEach(r => { if (r.learning_area_name) names.add(r.learning_area_name); });
        return [
            { name: 'All Learning Areas', value: 'all' },
            ...Array.from(names).sort().map(name => ({ name, value: name }))
        ];
    }, [learningAreas, records]);

    // 2. Grade & Stream Filter Options
    const gradeStreamOptions = useMemo(() => {
        const rawRecords = records?.results || records || [];
        const gradeMap = new Map();
        
        (learningAreas || []).forEach(la => {
            const gName = la.grade_level_name;
            const sName = la.stream_name || (la.stream_display_name && la.stream_display_name !== 'All Streams' ? la.stream_display_name : null);
            if (gName) {
                if (!gradeMap.has(gName)) gradeMap.set(gName, new Set());
                if (sName) gradeMap.get(gName).add(sName);
            }
        });

        rawRecords.forEach(r => {
            const gName = r.grade_level_name;
            const sName = r.stream_name;
            if (gName) {
                if (!gradeMap.has(gName)) gradeMap.set(gName, new Set());
                if (sName) gradeMap.get(gName).add(sName);
            }
        });

        const list = [{ name: 'All Grades & Streams', value: 'all' }];
        Array.from(gradeMap.keys()).sort().forEach(gName => {
            list.push({ name: `${gName} (All Streams)`, value: `grade_${gName}` });
            const streams = Array.from(gradeMap.get(gName)).sort();
            streams.forEach(sName => {
                list.push({ name: `${gName} • Stream ${sName}`, value: `stream_${gName}_${sName}` });
            });
        });
        return list;
    }, [learningAreas, records]);

    // 3. Week Filter Options
    const weekOptions = useMemo(() => [
        { name: 'All Weeks', value: '' },
        ...[...Array(14)].map((_, i) => ({ name: `Week ${i + 1}`, value: String(i + 1) }))
    ], []);

    const hasActiveFilters = (selectedLearningArea !== 'all') || (selectedGradeStream !== 'all') || Boolean(dateRange?.from || dateRange?.to) || selectedWeek !== '';

    const handleResetFilters = () => {
        setSelectedLearningArea('all');
        setSelectedGradeStream('all');
        setDateRange({ from: null, to: null });
        setSelectedWeek('');
        setSearchQuery('');
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <i className="fas fa-spinner fa-spin text-2xl text-indigo-600"></i>
            </div>
        );
    }

    const filteredRecords = (records?.results || records || []).filter(r => {
        const subStrand = r.sub_strand_name || '';
        const remarks = r.remarks || '';
        const teacher = r.teacher_name || '';
        const laName = r.learning_area_name || '';
        const gradeName = r.grade_level_name || '';
        const streamName = r.stream_name || '';

        // Search query match
        if (searchQuery.trim()) {
            const searchStr = `${subStrand} ${remarks} ${teacher} ${laName} ${gradeName} ${streamName}`.toLowerCase();
            if (!searchStr.includes(searchQuery.toLowerCase())) return false;
        }

        // Learning Area filter
        if (selectedLearningArea && selectedLearningArea !== 'all') {
            if (laName.toLowerCase() !== selectedLearningArea.toLowerCase()) return false;
        }

        // Grade & Stream filter
        if (selectedGradeStream && selectedGradeStream !== 'all') {
            if (selectedGradeStream.startsWith('stream_')) {
                const parts = selectedGradeStream.replace('stream_', '').split('_');
                const targetGrade = parts[0]?.toLowerCase();
                const targetStream = parts.slice(1).join('_').toLowerCase();
                if (targetGrade && gradeName.toLowerCase() !== targetGrade) return false;
                if (targetStream && (!streamName || !streamName.toLowerCase().includes(targetStream))) return false;
            } else if (selectedGradeStream.startsWith('grade_')) {
                const targetGrade = selectedGradeStream.replace('grade_', '').toLowerCase();
                if (gradeName.toLowerCase() !== targetGrade) return false;
            }
        }

        // Date Range filter
        const recordDate = r.lesson_date ? new Date(r.lesson_date) : null;
        if (recordDate && dateRange) {
            if (dateRange.from && recordDate < dateRange.from) return false;
            if (dateRange.to && recordDate > dateRange.to) return false;
        }

        // Week filter
        if (selectedWeek && String(r.week_number) !== String(selectedWeek)) {
            return false;
        }

        return true;
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between bg-white rounded-[1.5rem] px-6 py-5 border border-slate-100 shadow-sm gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600">
                            <DocumentTextIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-[#18216D] uppercase tracking-tight leading-tight">
                                Records of Work
                            </h2>
                            <p className="text-slate-500 font-medium text-xs mt-1">Pending records require reflections to be published.</p>
                        </div>
                    </div>
                </div>
                
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="relative group flex-1 sm:w-80 max-w-xs">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                            <MagnifyingGlassIcon className="w-5 h-5" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search records..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-sm font-bold text-[#18216D]"
                        />
                    </div>
                    {/* A teacher's records belong to one learning area, so the filters are for the HOS view only */}
                    {isHOS && (
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`p-2.5 rounded-xl transition-all border shadow-sm flex items-center gap-2 ${
                                showFilters
                                    ? 'bg-[#18216D] text-white border-transparent'
                                    : hasActiveFilters
                                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <FunnelIcon className="w-4 h-4" />
                            <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Filters</span>
                            {hasActiveFilters && (
                                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            )}
                        </button>
                    )}
                    <div className="flex bg-slate-50 border border-slate-200 rounded-xl p-1 shadow-sm">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-[#18216D]'}`}
                            title="Grid View"
                        >
                            <Squares2X2Icon className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('table')}
                            className={`p-1.5 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white text-[#18216D] shadow-sm' : 'text-slate-400 hover:text-[#18216D]'}`}
                            title="Table View"
                        >
                            <ListBulletIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {onSubTabChange && (
                <div className="flex justify-center border-b border-gray-200 mb-6">
                    <nav className="flex space-x-8" aria-label="Records & Lesson Plans Tabs">
                        <button
                            type="button"
                            onClick={() => onSubTabChange('lesson-plans')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${'lesson-plans' === subTab ? 'text-[#18216D] border-[#18216D]' : 'text-slate-400 hover:text-slate-600 border-transparent'}`}
                        >
                            <ClipboardDocumentListIcon className={`w-4 h-4 ${'lesson-plans' === subTab ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Lesson Plans</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => onSubTabChange('records')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${'records' === subTab ? 'text-[#18216D] border-[#18216D]' : 'text-slate-400 hover:text-slate-600 border-transparent'}`}
                        >
                            <DocumentTextIcon className={`w-4 h-4 ${'records' === subTab ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Records of Work</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => onSubTabChange('rubrics')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${'rubrics' === subTab ? 'text-[#18216D] border-[#18216D]' : 'text-slate-400 hover:text-slate-600 border-transparent'}`}
                        >
                            <AcademicCapIcon className={`w-4 h-4 ${'rubrics' === subTab ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Assessment Rubrics</span>
                        </button>
                    </nav>
                </div>
            )}

            {/* Pop-Out Filters Drawer (Matching Classes & Grading Pattern) */}
            {isHOS && showFilters && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-4 animate-fadeIn">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                        <div className="flex items-center gap-2">
                            <FunnelIcon className="w-4 h-4 text-[#18216D]" />
                            <span className="text-xs font-black text-[#18216D] uppercase tracking-wider">
                                Filter Records of Work
                            </span>
                            {hasActiveFilters && (
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-wider">
                                    Filtered
                                </span>
                            )}
                        </div>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="text-[11px] font-bold text-slate-400 hover:text-rose-600 transition-colors uppercase tracking-wider"
                            >
                                Reset Filters
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* 1. Learning Area Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Learning Area
                            </label>
                            <FilterDropdown
                                value={selectedLearningArea}
                                onChange={(val) => setSelectedLearningArea(val || 'all')}
                                options={learningAreaOptions}
                                placeholder="All Learning Areas"
                                className="w-full"
                            />
                        </div>

                        {/* 2. Grade & Stream Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Grade &amp; Stream
                            </label>
                            <FilterDropdown
                                value={selectedGradeStream}
                                onChange={(val) => setSelectedGradeStream(val || 'all')}
                                options={gradeStreamOptions}
                                placeholder="All Grades &amp; Streams"
                                className="w-full"
                            />
                        </div>

                        {/* 3. Date Range Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Date Range
                            </label>
                            <DateRangePicker 
                                dateRange={dateRange}
                                onDateRangeChange={(val) => setDateRange(val)}
                            />
                        </div>

                        {/* 4. Week Filter */}
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                                Week
                            </label>
                            <FilterDropdown
                                value={selectedWeek}
                                onChange={(val) => setSelectedWeek(val || '')}
                                options={weekOptions}
                                placeholder="All Weeks"
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            )}

            {filteredRecords.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm">
                    <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <DocumentTextIcon className="w-10 h-10 text-slate-300" />
                    </div>
                    <h3 className="text-xl font-bold text-[#18216D] mb-2">No Records Found</h3>
                    <p className="text-slate-400 font-medium max-w-md mx-auto">
                        No records of work match your current filters.
                    </p>
                </div>
            ) : viewMode === 'grid' ? (
                <div className="space-y-4">
                    {filteredRecords.map((record) => {
                        const cleanActivities = sanitizeText(record.learning_activities);
                        const cleanRemarks = sanitizeText(record.remarks);

                        return (
                            <div key={record.id} className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm p-6 flex flex-col md:flex-row gap-6 hover:shadow-md transition-shadow">
                                <div className="flex-1 space-y-4">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                <span className="px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest">
                                                    Week {record.week_number}
                                                </span>
                                                <div className="flex items-center gap-1.5 text-slate-500 text-xs font-semibold bg-slate-50 px-2.5 py-1 rounded-lg">
                                                    <CalendarIcon className="w-3.5 h-3.5" />
                                                    {record.lesson_date || 'Unscheduled'}
                                                </div>
                                                {isHOS && record.teacher_name && (
                                                    <div className="flex items-center gap-1.5 text-[#18216D] text-xs font-black bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-lg">
                                                        <UserCircleIcon className="w-3.5 h-3.5 text-amber-500" />
                                                        {record.teacher_name}
                                                    </div>
                                                )}
                                            </div>
                                            <h3 className="text-lg font-black text-[#18216D] leading-tight">
                                                {record.lesson_title || record.sub_strand_name || 'Untitled Lesson'}
                                            </h3>
                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-1">
                                                {[record.strand_name, record.sub_strand_name].filter(Boolean).join(' • ')}
                                            </p>
                                            {isHOS && (record.learning_area_name || record.grade_level_name) && (
                                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                                    {record.learning_area_name && (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 border border-slate-100 rounded-lg text-[10px] font-black uppercase tracking-widest text-[#18216D]">
                                                            <span className="text-slate-400">Learning Area</span>
                                                            {record.learning_area_name}
                                                        </span>
                                                    )}
                                                    {record.grade_level_name && (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-100 rounded-lg text-[10px] font-black uppercase tracking-widest text-emerald-700">
                                                            <span className="text-emerald-500">Grade &amp; Stream</span>
                                                            {record.grade_level_name}{record.stream_name ? ` - ${record.stream_name}` : ''}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-shrink-0 flex flex-col items-end gap-3">
                                            {cleanRemarks ? (
                                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-xl text-xs font-bold border border-emerald-100">
                                                    <CheckBadgeIcon className="w-4 h-4" /> Published
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-600 rounded-xl text-xs font-bold border border-amber-100">
                                                    <ClockIcon className="w-4 h-4" /> Pending
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2">
                                                <PremiumTooltip text="Download PDF" allowWrap={false}>
                                                    <button
                                                        onClick={() => handleExportRecord(record)}
                                                        className="p-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-all border border-transparent shadow-sm"
                                                    >
                                                        <ArrowDownTrayIcon className="w-4 h-4 stroke-[2.5]" />
                                                    </button>
                                                </PremiumTooltip>
                                                {record.lesson_plan && (
                                                    <button
                                                        onClick={() => openPlanEditor(record.lesson_plan)}
                                                        className="px-4 py-1.5 bg-indigo-50 text-[#18216D] hover:bg-[#18216D] hover:text-white transition-all rounded-xl text-[10px] font-black uppercase tracking-widest border border-indigo-100 hover:border-transparent"
                                                    >
                                                        Open Plan
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-50">
                                        <div className="space-y-1.5">
                                            <div className="flex items-center gap-1.5 text-slate-400">
                                                <BookOpenIcon className="w-3.5 h-3.5" />
                                                <span className="text-[10px] font-black uppercase tracking-widest">Activities</span>
                                            </div>
                                            <p className="text-sm font-medium text-slate-600 line-clamp-3">
                                                {cleanActivities || 'No activities logged.'}
                                            </p>
                                        </div>
                                        <div className="space-y-1.5">
                                            <div className="flex items-center gap-1.5 text-slate-400">
                                                <DocumentTextIcon className="w-3.5 h-3.5" />
                                                <span className="text-[10px] font-black uppercase tracking-widest">Teacher's Remarks</span>
                                            </div>
                                            <p className="text-sm font-medium text-slate-600 italic line-clamp-3">
                                                {cleanRemarks ? `"${cleanRemarks}"` : 'No remarks added yet.'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Week</th>
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Date</th>
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[150px]">Strand</th>
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[180px]">Sub-strand</th>
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[200px]">Activities</th>
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[200px]">Teacher's Remarks</th>
                                    {isHOS && <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Teacher</th>}
                                    <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center whitespace-nowrap">Status & Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredRecords.map((record) => {
                                    const cleanActivities = sanitizeText(record.learning_activities);
                                    const cleanRemarks = sanitizeText(record.remarks);

                                    return (
                                        <tr key={record.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="py-4 px-6">
                                                <span className="px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
                                                    W{record.week_number || '?'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-1.5 text-slate-500 text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
                                                    <CalendarIcon className="w-3.5 h-3.5" />
                                                    {record.lesson_date || 'Unscheduled'}
                                                </div>
                                            </td>
                                            <td className="py-4 px-6 max-w-xs">
                                                <PremiumTooltip text={record.strand_name || ''} allowWrap={true} wrapperClassName="relative inline-flex max-w-full">
                                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider line-clamp-2">
                                                        {record.strand_name || 'N/A'}
                                                    </p>
                                                </PremiumTooltip>
                                            </td>
                                            <td className="py-4 px-6 max-w-xs">
                                                <PremiumTooltip text={record.sub_strand_name || 'Unnamed Sub-strand'} allowWrap={true} wrapperClassName="relative inline-flex max-w-full">
                                                    <h3 className="text-sm font-black text-[#18216D] leading-tight line-clamp-2">
                                                        {record.sub_strand_name || 'Unnamed Sub-strand'}
                                                    </h3>
                                                </PremiumTooltip>
                                            </td>
                                            <td className="py-4 px-6 max-w-xs">
                                                {cleanActivities ? (
                                                    <PremiumTooltip text={cleanActivities} allowWrap={true} wrapperClassName="relative inline-flex max-w-full">
                                                        <p className="text-xs font-medium text-slate-600 line-clamp-2">
                                                            {cleanActivities}
                                                        </p>
                                                    </PremiumTooltip>
                                                ) : (
                                                    <span className="text-xs font-medium text-slate-300 italic">No activities logged.</span>
                                                )}
                                            </td>
                                            <td className="py-4 px-6 max-w-xs">
                                                {cleanRemarks ? (
                                                    <PremiumTooltip text={cleanRemarks} allowWrap={true} wrapperClassName="relative inline-flex max-w-full">
                                                        <p className="text-xs font-medium text-slate-600 italic line-clamp-2">
                                                            "{cleanRemarks}"
                                                        </p>
                                                    </PremiumTooltip>
                                                ) : (
                                                    <span className="text-xs font-medium text-slate-300 italic">No remarks added yet.</span>
                                                )}
                                            </td>
                                            {isHOS && (
                                                <td className="py-4 px-6">
                                                    {record.teacher_name && (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                                                                <UserCircleIcon className="w-4 h-4 text-amber-500" />
                                                            </div>
                                                            <span className="text-xs font-black text-[#18216D] whitespace-nowrap">
                                                                {record.teacher_name}
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                            <td className="py-4 px-6 text-center">
                                                <div className="flex flex-col items-center gap-2">
                                                    {cleanRemarks ? (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[10px] font-black uppercase tracking-widest border border-emerald-100 whitespace-nowrap">
                                                            <CheckBadgeIcon className="w-3.5 h-3.5" /> Published
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-600 rounded-lg text-[10px] font-black uppercase tracking-widest border border-amber-100 whitespace-nowrap">
                                                            <ClockIcon className="w-3.5 h-3.5" /> Pending
                                                        </span>
                                                    )}
                                                    <div className="flex items-center gap-2">
                                                        <PremiumTooltip text="Download PDF" allowWrap={false}>
                                                            <button
                                                                onClick={() => handleExportRecord(record)}
                                                                className="p-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-all border border-transparent shadow-sm"
                                                            >
                                                                <ArrowDownTrayIcon className="w-4 h-4 stroke-[2.5]" />
                                                            </button>
                                                        </PremiumTooltip>
                                                        {record.lesson_plan && (
                                                            <button
                                                                onClick={() => openPlanEditor(record.lesson_plan)}
                                                                className="px-4 py-1.5 bg-white text-[#18216D] hover:bg-[#18216D] hover:text-white transition-all rounded-xl text-[10px] font-black uppercase tracking-widest border border-slate-200 hover:border-transparent shadow-sm whitespace-nowrap"
                                                            >
                                                                Open Plan
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecordOfWorkTab;
